/* Dataset → Overview: import, reconciliation report, series catalogue.
 *
 * The reconciliation report is deliberately shown, not hidden: the export
 * cannot be read with a CSV reader, so the per-year alignment shape and the
 * mass-balance residual are the evidence that the load is trustworthy.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  API, NATURE_IDS, NatureChip, ConfirmBar, natureMeta,
  fmtDateTime, fmtPct, spanLabel, statusColor,
} from './datasetCommon';

const ACTIVE = ['staged', 'reconciling', 'loading', 'compressing', 'aggregating'];

const CATALOGUE_COLUMNS = [
  { key: 'col_index',    label: '#',        width: 44,  align: 'right' },
  { key: 'plant',        label: 'Plant',    width: 200 },
  { key: 'kategorie',    label: 'Kategorie', width: 150 },
  { key: 'resource',     label: 'Resource', width: 130 },
  { key: 'unit',         label: 'Unit',     width: 110 },
  { key: 'nature',       label: 'Nature',   width: 170 },
  { key: 'is_stock',     label: 'Stock',    width: 60,  align: 'center' },
  { key: 'display_name', label: 'Display name', width: 260 },
];

function ImportPanel({ projectId, onStarted }) {
  const energyRef = useRef(null);
  const overviewRef = useRef(null);
  const [energyFile, setEnergyFile] = useState(null);
  const [overviewFile, setOverviewFile] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const start = async () => {
    if (!energyFile || !overviewFile) { setError('Both the energy CSV and the overview CSV are required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('energy_csv', energyFile);
      fd.append('overview_csv', overviewFile);
      if (name.trim()) fd.append('name', name.trim());
      const res = await fetch(`${API}/api/projects/${projectId}/datasets`, { method: 'POST', body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const created = await res.json();
      setEnergyFile(null); setOverviewFile(null); setName('');
      if (energyRef.current) energyRef.current.value = '';
      if (overviewRef.current) overviewRef.current.value = '';
      onStarted(created);
    } catch (err) {
      setError(`Import failed: ${err.message || String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ds-panel">
      <div className="ds-panel-head">
        <h3>Import</h3>
        <span className="ds-muted">energyPRO time-series export + the annual overview (.csv or .csv.gz)</span>
      </div>
      <div className="ds-import-grid">
        <label className="ds-field">
          <span>Energy CSV</span>
          <input ref={energyRef} type="file" accept=".csv,.gz,.csv.gz" className="ds-input"
                 onChange={(e) => setEnergyFile(e.target.files[0] || null)} />
        </label>
        <label className="ds-field">
          <span>Overview CSV</span>
          <input ref={overviewRef} type="file" accept=".csv,.gz,.csv.gz" className="ds-input"
                 onChange={(e) => setOverviewFile(e.target.files[0] || null)} />
        </label>
        <label className="ds-field">
          <span>Name (optional)</span>
          <input type="text" className="ds-input" placeholder="V6_Green_NH4NO3 40a"
                 value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="ds-field" style={{ justifyContent: 'flex-end' }}>
          <span>&nbsp;</span>
          <button className="btn-accent" onClick={start} disabled={busy || !energyFile || !overviewFile}>
            {busy ? 'Uploading…' : 'Start import'}
          </button>
        </div>
      </div>
      {error && <div className="ds-error">{error}</div>}
    </div>
  );
}

function ProgressStrip({ dataset, onResume, onDelete, deleting, confirming, setConfirming }) {
  const pct = dataset.rows_expected
    ? Math.min(100, ((dataset.rows_offset || 0) / dataset.rows_expected) * 100)
    : null;
  const running = ACTIVE.includes(dataset.status);
  const color = statusColor(dataset.status);

  return (
    <div className="ds-panel">
      <div className="ds-panel-head">
        <h3>
          <span style={{ color, marginRight: 8 }}>●</span>
          {dataset.name || 'unnamed dataset'}
        </h3>
        <div style={{ flex: 1 }} />
        {dataset.status === 'failed' && (
          <button className="btn-accent" onClick={onResume}>Resume</button>
        )}
        <button className="ds-btn-danger" onClick={() => setConfirming(true)} disabled={confirming}>
          Delete dataset
        </button>
      </div>

      <div className="ds-kv">
        <div><span>Status</span><strong style={{ color }}>{dataset.status}</strong></div>
        <div><span>Tool</span><strong>{dataset.tool || '—'}</strong></div>
        <div><span>Exported</span><strong>{dataset.exported_at ? fmtDateTime(dataset.exported_at) : '—'}</strong></div>
        <div><span>Timezone</span><strong>{dataset.tz || '—'}</strong></div>
        <div><span>Step</span><strong>{dataset.step_minutes ? `${dataset.step_minutes} min` : '—'}</strong></div>
        <div><span>Span</span><strong>{spanLabel(dataset) || '—'}</strong></div>
        <div><span>Series</span><strong>{dataset.series_count ?? '—'}</strong></div>
        <div><span>Rows loaded</span><strong>{(dataset.rows_loaded || 0).toLocaleString()}</strong></div>
        <div><span>Compressed</span><strong>{dataset.compressed_bytes ? `${(dataset.compressed_bytes / 1048576).toFixed(1)} MB` : '—'}</strong></div>
        <div><span>Created</span><strong>{fmtDateTime(dataset.created_at)}</strong></div>
        <div><span>Finished</span><strong>{dataset.finished_at ? fmtDateTime(dataset.finished_at) : '—'}</strong></div>
      </div>

      {(running || pct !== null) && (
        <div className="ds-progress-wrap">
          <div className="ds-progress">
            <div className="ds-progress-bar"
                 style={{ width: `${pct === null ? 0 : pct}%`, background: color }} />
          </div>
          <span className="ds-muted">
            {(dataset.rows_offset || 0).toLocaleString()} / {(dataset.rows_expected || 0).toLocaleString()} rows
            {pct !== null ? ` · ${pct.toFixed(1)} %` : ''}
          </span>
        </div>
      )}

      {dataset.status === 'failed' && dataset.error && (
        <div className="ds-error">{dataset.error}</div>
      )}

      {confirming && (
        <ConfirmBar
          message={`Delete "${dataset.name || 'this dataset'}" and every point it loaded? This cannot be undone.`}
          onConfirm={onDelete}
          onCancel={() => setConfirming(false)}
          busy={deleting}
        />
      )}
    </div>
  );
}

function ReconciliationReport({ report }) {
  if (!report) return null;
  const pass = report.reconciliation === 'pass';
  const years = Object.entries(report.alignment || {}).sort((a, b) => a[0].localeCompare(b[0]));
  const shapes = Object.entries(report.alignment_shapes || {});

  return (
    <div className="ds-panel">
      <div className="ds-panel-head">
        <h3>Reconciliation</h3>
      </div>

      <div className="ds-verdict" style={{ borderColor: pass ? '#4ecca3' : '#f87171' }}>
        <span style={{ color: pass ? '#4ecca3' : '#f87171', fontWeight: 700 }}>
          {pass ? 'PASS' : 'FAIL'}
        </span>
        <span className="ds-muted">
          {report.arbitrated_agreed ?? '—'} / {report.arbitrated_checks ?? '—'} arbitrated checks agreed
          {' · '}worst balance residual {fmtPct(report.worst_balance_residual)}
          {report.series_in_header !== undefined ? ` · ${report.series_in_header} series in header` : ''}
          {report.ragged_rows !== undefined ? ` · ${Number(report.ragged_rows).toLocaleString()} ragged rows` : ''}
        </span>
      </div>

      {shapes.length > 0 && (
        <div className="ds-chip-row">
          {shapes.map(([shape, n]) => (
            <span key={shape} className="ds-chip" style={{ borderColor: '#334155', color: '#cbd5e1' }}>
              {shape} · {n}
            </span>
          ))}
        </div>
      )}

      {years.length > 0 && (
        <div className="ds-table-scroll" style={{ maxHeight: 300 }}>
          <table className="ds-table">
            <thead>
              <tr>
                <th>Year</th><th>Shape</th><th>Aligned</th><th>Unheaded column</th>
                <th>Absent series</th><th>Arbitrated / agreed</th>
                <th style={{ textAlign: 'right' }}>Balance residual</th><th>Worst resource</th>
              </tr>
            </thead>
            <tbody>
              {years.map(([year, a]) => (
                <tr key={year} className={a.aligned === false ? 'ds-row-bad' : undefined}>
                  <td>{year}</td>
                  <td>{a.shape || '—'}</td>
                  <td>{a.aligned === undefined ? '—' : (a.aligned ? 'yes' : 'no')}</td>
                  <td>{a.unheaded_column === null || a.unheaded_column === undefined ? '—' : String(a.unheaded_column)}</td>
                  <td>{Array.isArray(a.absent_series) ? a.absent_series.join(', ') || '—' : (a.absent_series ?? '—')}</td>
                  <td>{a.arbitrated ?? '—'} / {a.agreed ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtPct(a.balance_residual)}</td>
                  <td>{a.worst_resource || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Array.isArray(report.corrections_applied) && report.corrections_applied.length > 0 && (
        <div className="ds-corrections">
          <div className="ds-sub">Corrections applied</div>
          <ul>
            {report.corrections_applied.map((c, i) => (
              <li key={i}>
                <strong>{c.correction || 'correction'}</strong>
                {c.why ? ` — ${c.why}` : ''}
                {Object.entries(c)
                  .filter(([k]) => k !== 'correction' && k !== 'why')
                  .map(([k, v]) => ` · ${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                  .join('')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SeriesCatalogue({ series, onPatch }) {
  const [sort, setSort] = useState({ key: 'col_index', dir: 1 });
  const [draft, setDraft] = useState({});   // sid -> in-flight display_name text

  const rows = useMemo(() => {
    const copy = [...series];
    copy.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
    return copy;
  }, [series, sort]);

  const head = (c) => (
    <th key={c.key}
        style={{ width: c.width, textAlign: c.align || 'left', cursor: 'pointer' }}
        onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}>
      {c.label}{sort.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div className="ds-panel">
      <div className="ds-panel-head">
        <h3>Series catalogue</h3>
        <span className="ds-muted">{series.length} series · nature and display name are editable and audited</span>
      </div>
      <div className="ds-table-scroll" style={{ maxHeight: 460 }}>
        <table className="ds-table">
          <thead><tr>{CATALOGUE_COLUMNS.map(head)}</tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td style={{ textAlign: 'right' }}>{s.col_index}</td>
                <td>{s.plant || '—'}</td>
                <td>{s.kategorie || '—'}</td>
                <td>{s.resource || '—'}</td>
                <td title={s.declared_unit ? `declared: ${s.declared_unit}` : undefined}>
                  {s.unit || '—'}
                  {s.unit_source && (
                    <span className="ds-unit-src" title={`unit resolved from ${s.unit_source}`}>
                      {s.unit_source === 'overview' ? 'ov' : 'inf'}
                    </span>
                  )}
                </td>
                <td>
                  <select
                    className="ds-input ds-input-sm"
                    value={s.nature || 'unassigned'}
                    onChange={(e) => onPatch(s.id, { nature: e.target.value })}
                    style={{ borderColor: natureMeta(s.nature).color }}
                  >
                    {NATURE_IDS.map((n) => <option key={n} value={n}>{natureMeta(n).label} ({n})</option>)}
                  </select>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {s.is_stock ? <span className="ds-stock-flag" title="stock — never summed">S</span> : '—'}
                </td>
                <td>
                  <input
                    className="ds-input ds-input-sm"
                    value={draft[s.id] !== undefined ? draft[s.id] : (s.display_name || '')}
                    onChange={(e) => setDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                    onBlur={() => {
                      const v = draft[s.id];
                      setDraft((d) => { const n = { ...d }; delete n[s.id]; return n; });
                      if (v !== undefined && v !== (s.display_name || '')) onPatch(s.id, { display_name: v });
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DatasetOverviewTab = ({
  project, datasets, dataset, series, onStarted, onResume, onDelete, onPatchSeries, deleting,
}) => {
  const [confirming, setConfirming] = useState(false);
  const datasetId = dataset && dataset.id;
  useEffect(() => { setConfirming(false); }, [datasetId]);

  const natures = (dataset && dataset.natures) || {};
  const chips = NATURE_IDS.filter((n) => natures[n]);

  return (
    <div className="ds-tab-body">
      <ImportPanel projectId={project.id} onStarted={onStarted} />

      {datasets.length === 0 && (
        <div className="ds-empty">No dataset loaded for this project yet.</div>
      )}

      {dataset && (
        <>
          <ProgressStrip
            dataset={dataset}
            onResume={onResume}
            onDelete={onDelete}
            deleting={deleting}
            confirming={confirming}
            setConfirming={setConfirming}
          />
          {chips.length > 0 && (
            <div className="ds-chip-row">
              {chips.map((n) => <NatureChip key={n} nature={n} count={natures[n]} />)}
            </div>
          )}
          <ReconciliationReport report={dataset.report} />
          {series.length > 0 && <SeriesCatalogue series={series} onPatch={onPatchSeries} />}
        </>
      )}
    </div>
  );
};

export default DatasetOverviewTab;
